import React from 'react'
import { useTranslation } from 'react-i18next'

import { boxShadowFloat } from '@/_vars'
import { appConfig } from '@/app-config/appConfig'
import { requestProviderLogout } from '@/lib/auth'
import { getCurrentLocale } from '@/lib/i18n/locale-utils'
import { isProductionEnvironment } from '@/lib/isProductionEnvironment'
import { openExternalUrl } from '@/lib/openExternalUrl'
import { useToggle } from '@/lib/react-hooks/useToggle'
import { redirectTo, routeFor } from '@/lib/route-utils'
import { Routes } from '@/lib/routes'
import { eventStopper } from '@/lib/utils'
import { FeatureFlagSidebar } from '@/molecules/FeatureFlagSidebar/FeatureFlagSidebar'
import { getStore } from '@/redux/stores'
import { fontBlack, hoverBlue, white } from '@/refactor/colors'
import { font } from '@/refactor/fonts'
import styled, { animations, muiUtils, useIsMobile } from '@/styles'
import { DefaultProps } from '@/travelsuit'
import { PrivacyPolicyLinkByLanguage } from '@/travelsuit/locations'
import { User } from '@/types/user'

import Box from '../Box/Box'
import PolicyPreviewLink from '../PolicyPreviewLink/PolicyPreviewLink'

interface IProps extends DefaultProps {
	open?: boolean
	user?: User | null
	onClickMenuItem?(): void
}

const LoginMenuContainer = styled(Box).attrs(() => ({ radius: 5 }))<{ open?: boolean }>`
	padding: 0;
	color: ${fontBlack};
	opacity: 0;
	pointer-events: none;
	min-width: 150px;
	${({ open: $open }) => ($open ? animations.slideInTop.animate(300) : '')}

	${({ open: $open }) =>
		$open
			? `
				opacity: 1;
				pointer-events: all;
				`
			: ''}

	${muiUtils.mobile} {
		position: static;
		top: initial;
		right: initial;
		background: none;
		width: 100%;
	}

	${muiUtils.desktop} {
		position: absolute;
		margin: 0;
		background: ${white};
		top: calc(100% + 40px);
		right: 0;
		transform-origin: top right;
		z-index: 20000;
		box-shadow: ${boxShadowFloat};
	}
`

const MenuItem = styled.a<{ support?: boolean }>`
	transition: all 150ms ease-in-out;
	cursor: pointer;
	${font({ size: 14 })}
	padding: 15px 20px;
	color: currentColor;
	text-decoration: none;
	display: block;

	white-space: pre;

	&:hover,
	&:active {
		background: ${hoverBlue};
	}

	${({ support: $support }) =>
		$support
			? `
				background: rgb(83, 109, 254);
				color: ${white};
			`
			: ''}
`

const FeatureFlagMenuItem = () => {
	const [isOpen, toggleIsOpen] = useToggle(false)

	if (isProductionEnvironment() || !appConfig.FEATURE_DEBUG_INFO) {
		return null
	}

	return (
		<>
			<MenuItem onClick={toggleIsOpen}>{'Company Feature Flags'}</MenuItem>
			{isOpen && <FeatureFlagSidebar onClose={toggleIsOpen} />}
		</>
	)
}

function UserLoginMenu(props: IProps) {
	const { className, user, ...rest } = props
	const isSupportUser: boolean = false // TODO: find a way to determine if user has support capability
	const aTarget = useIsMobile() ? '_blank' : undefined

	function logout() {
		requestProviderLogout(getStore())
	}

	function openPrivacyPolicy() {
		openExternalUrl(PrivacyPolicyLinkByLanguage[getCurrentLocale()], '_blank')

		if (props.onClickMenuItem) {
			props.onClickMenuItem()
		}
	}

	function openTravelerProfile() {
		if (props.onClickMenuItem) {
			props.onClickMenuItem()
		}

		redirectTo(Routes.EditTravelerProfile, { id: props.user!.id })
	}

	function openSupport() {
		if (props.onClickMenuItem) {
			props.onClickMenuItem()
		}

		redirectTo(Routes.Support)
	}

	const { t } = useTranslation()

	return (
		<LoginMenuContainer className={className} open={rest.open}>
			{user ? (
				<>
					<MenuItem
						href={routeFor(Routes.EditTravelerProfile, { id: props.user!.id })}
						target={aTarget}
						data-test="MenuItem.EditProfile"
						onClick={eventStopper.preventDefault(openTravelerProfile)}
					>
						{t('user-menu.edit')}
					</MenuItem>
					<PolicyPreviewLink
						users={[user]}
						render={({ onClick }) => (
							<MenuItem data-test="MenuItem.MyTravelPolicy" onClick={eventStopper.preventDefault(onClick)}>
								{t('user-menu.my-policy')}
							</MenuItem>
						)}
					/>
				</>
			) : null}
			<MenuItem data-test="MenuItem.PrivacyPolicy" onClick={eventStopper.preventDefault(() => openPrivacyPolicy())}>
				{t('user-menu.privacy')}
			</MenuItem>
			{isSupportUser ? (
				<MenuItem support href={Routes.Support} onClick={eventStopper.preventDefault(() => openSupport())}>
					{t('user-menu.support')}
				</MenuItem>
			) : null}
			<FeatureFlagMenuItem />
			<MenuItem
				href={Routes.Logout}
				target={aTarget}
				data-test="MenuItem.Logout"
				onClick={eventStopper.preventDefault(() => logout())}
			>
				{t('user-menu.logout')}
			</MenuItem>
		</LoginMenuContainer>
	)
}

export default UserLoginMenu
